import React, { useState, useEffect } from 'react';
import { DollarSign, Save } from 'lucide-react';
import { usePricing, BasePrice } from '@context/PricingContext';
import Button from '@components/common/Button';
import Input from '@components/common/Input';
import Alert from '@components/common/Alert';

interface PricingFormProps {
  onDataChange?: () => void;
  onSaveSuccess?: () => void;
}

const PricingForm: React.FC<PricingFormProps> = ({ onDataChange, onSaveSuccess }) => {
  const { configuration, loading, error, updateBasePrices } = usePricing();
  const [formData, setFormData] = useState<BasePrice>({
    singlePhoto: 500,
    smallBundle: 1500,
    mediumBundle: 2500,
    largeBundle: 4000,
    fullEvent: 7500,
  });
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (configuration?.basePrices) {
      setFormData(configuration.basePrices);
    }
  }, [configuration?.basePrices]);

  const handleInputChange = (field: keyof BasePrice, value: string) => {
    const numericValue = Math.max(0, parseInt(value) || 0);
    setFormData(prev => ({
      ...prev,
      [field]: numericValue,
    }));
    setSaveSuccess(false);
    onDataChange?.();
  };

  const formatCurrency = (cents: number): string => {
    return (cents / 100).toFixed(2);
  };


  const handleSave = async () => {
    setSaveLoading(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      await updateBasePrices(formData);
      setSaveSuccess(true);
      onSaveSuccess?.();
    } catch (err: any) {
      setSaveError(err.message || 'Failed to save pricing');
    } finally {
      setSaveLoading(false);
    }
  };

  const calculateSavings = () => {
    const singlePhotoPrice = formData.singlePhoto;
    return {
      smallBundle: {
        individual: singlePhotoPrice * 5,
        bundle: formData.smallBundle,
        savings: (
          ((singlePhotoPrice * 5 - formData.smallBundle) / (singlePhotoPrice * 5)) *
          100
        ).toFixed(1),
      },
      mediumBundle: {
        individual: singlePhotoPrice * 10,
        bundle: formData.mediumBundle,
        savings: (
          ((singlePhotoPrice * 10 - formData.mediumBundle) / (singlePhotoPrice * 10)) *
          100
        ).toFixed(1),
      },
      largeBundle: {
        individual: singlePhotoPrice * 20,
        bundle: formData.largeBundle,
        savings: (
          ((singlePhotoPrice * 20 - formData.largeBundle) / (singlePhotoPrice * 20)) *
          100
        ).toFixed(1),
      },
    };
  };

  const savings = calculateSavings();

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center">
          <DollarSign className="h-5 w-5 text-blue-600 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">Base Pricing</h2>
        </div>
        <p className="text-sm text-gray-600 mt-1">
          Set your base prices for individual photos and bundles.
        </p>
      </div>

      <div className="p-6 space-y-6">
        {error && <Alert type="error" title="Error" message={error} />}

        {saveError && <Alert type="error" title="Save Failed" message={saveError} />}

        {saveSuccess && (
          <Alert
            type="success"
            title="Saved Successfully"
            message="Your pricing has been updated."
          />
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Individual Photo */}
          <div>
            <Input
              label="Single Photo"
              type="text"
              value={formatCurrency(formData.singlePhoto)}
              onChange={e => handleInputChange('singlePhoto', e.target.value)}
              leftIcon={<DollarSign className="h-4 w-4" />}
              helperText="Price per individual photo"
              placeholder="5.00"
            />
          </div>

          <div className="hidden md:block" />

          {/* Small Bundle */}
          <div>
            <Input
              label="Small Bundle (5 photos)"
              type="text"
              value={formatCurrency(formData.smallBundle)}
              onChange={e => handleInputChange('smallBundle', e.target.value)}
              leftIcon={<DollarSign className="h-4 w-4" />}
              helperText={`${savings.smallBundle.savings}% savings vs individual`}
              placeholder="15.00"
            />
          </div>

          <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
            <div className="font-medium">
              Individual: ${formatCurrency(savings.smallBundle.individual)}
            </div>
            <div className="text-green-600 font-medium">
              Bundle: ${formatCurrency(formData.smallBundle)}
            </div>
            <div className="text-green-600">
              Save: ${formatCurrency(savings.smallBundle.individual - formData.smallBundle)} (
              {savings.smallBundle.savings}%)
            </div>
          </div>

          {/* Medium Bundle */}
          <div>
            <Input
              label="Medium Bundle (10 photos)"
              type="text"
              value={formatCurrency(formData.mediumBundle)}
              onChange={e => handleInputChange('mediumBundle', e.target.value)}
              leftIcon={<DollarSign className="h-4 w-4" />}
              helperText={`${savings.mediumBundle.savings}% savings vs individual`}
              placeholder="25.00"
            />
          </div>

          <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
            <div className="font-medium">
              Individual: ${formatCurrency(savings.mediumBundle.individual)}
            </div>
            <div className="text-green-600 font-medium">
              Bundle: ${formatCurrency(formData.mediumBundle)}
            </div>
            <div className="text-green-600">
              Save: ${formatCurrency(savings.mediumBundle.individual - formData.mediumBundle)} (
              {savings.mediumBundle.savings}%)
            </div>
          </div>

          {/* Large Bundle */}
          <div>
            <Input
              label="Large Bundle (20 photos)"
              type="text"
              value={formatCurrency(formData.largeBundle)}
              onChange={e => handleInputChange('largeBundle', e.target.value)}
              leftIcon={<DollarSign className="h-4 w-4" />}
              helperText={`${savings.largeBundle.savings}% savings vs individual`}
              placeholder="40.00"
            />
          </div>

          <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
            <div className="font-medium">
              Individual: ${formatCurrency(savings.largeBundle.individual)}
            </div>
            <div className="text-green-600 font-medium">
              Bundle: ${formatCurrency(formData.largeBundle)}
            </div>
            <div className="text-green-600">
              Save: ${formatCurrency(savings.largeBundle.individual - formData.largeBundle)} (
              {savings.largeBundle.savings}%)
            </div>
          </div>

          {/* Full Event */}
          <div>
            <Input
              label="Full Event Package"
              type="text"
              value={formatCurrency(formData.fullEvent)}
              onChange={e => handleInputChange('fullEvent', e.target.value)}
              leftIcon={<DollarSign className="h-4 w-4" />}
              helperText="Price for all photos from the event"
              placeholder="75.00"
            />
          </div>

          <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-700">
            <div className="font-medium">Best Value Package</div>
            <div>Customers get all event photos</div>
            <div>Highest perceived value</div>
          </div>
        </div>

        {/* Commission Info */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="text-sm text-yellow-800">
            <div className="font-medium mb-1">Commission Information</div>
            <div>
              You'll receive{' '}
              {configuration ? Math.round(configuration.photographerCommission * 100) : 70}% of each
              sale after payment processing fees.
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            loading={saveLoading}
            disabled={loading}
            icon={<Save className="h-4 w-4" />}
          >
            Save Pricing
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PricingForm;
